/**
 * Dashboards (M12): a 12-column grid of widgets, owned by a user, optionally
 * shared. The widget list is one JSONB value on the dashboard row and is
 * always saved whole (optimistic lock on `version`), so its shape is
 * validated here, once, for the server command and the browser editor.
 */
import { z } from "zod";
export const DASHBOARD_COLS = 12;
/** Grid row height in px; widths are grid columns of the page width. */
export const DASHBOARD_ROW_PX = 80;
export const MAX_WIDGETS = 30;
/** Serialized `widgets` cap (Notes may embed data: images). */
export const MAX_WIDGETS_BYTES = 2 * 1024 * 1024;
const Title = z.string().max(200).optional();
const Query = z.string().max(4000);
export const IssuesWidgetConfig = z.object({ title: Title, query: Query, limit: z.number().int().min(5).max(50).default(10) });
export const NotesWidgetConfig = z.object({ title: Title, markdown: z.string().max(1_000_000) });
export const CountersWidgetConfig = z.object({
    title: Title,
    items: z.array(z.object({ label: z.string().min(1).max(100), query: Query })).min(1).max(12),
});
export const DistributionWidgetConfig = z.object({ title: Title, query: Query, by: z.string().min(1).max(200), limit: z.number().int().min(1).max(20).default(10) });
const placed = (type, config) => z.object({
    id: z.string().min(1).max(32),
    type: z.literal(type),
    x: z.number().int().min(0).max(DASHBOARD_COLS - 1),
    y: z.number().int().min(0).max(10_000),
    w: z.number().int().min(3).max(DASHBOARD_COLS),
    h: z.number().int().min(2).max(12),
    config,
});
export const DashboardWidget = z
    .discriminatedUnion("type", [placed("issues", IssuesWidgetConfig), placed("notes", NotesWidgetConfig), placed("counters", CountersWidgetConfig), placed("distribution", DistributionWidgetConfig)])
    .refine((w) => w.x + w.w <= DASHBOARD_COLS, { message: `x + w must be at most ${DASHBOARD_COLS}`, path: ["w"] });
export const DashboardWidgets = z
    .array(DashboardWidget)
    .max(MAX_WIDGETS)
    .refine((ws) => new Set(ws.map((w) => w.id)).size === ws.length, { message: "widget ids must be unique" })
    .refine((ws) => JSON.stringify(ws).length <= MAX_WIDGETS_BYTES, { message: `widgets exceed ${MAX_WIDGETS_BYTES} bytes` });
const Ids = z.array(z.number().int()).max(500).default([]);
export const DashboardAccess = z.object({
    view: z.object({ everyone: z.boolean().default(false), users: Ids, groups: Ids }).default({}),
    edit: z.object({ users: Ids, groups: Ids }).default({}),
});
export const NO_ACCESS = { view: { everyone: false, users: [], groups: [] }, edit: { users: [], groups: [] } };
//# sourceMappingURL=dashboards.js.map